import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Colorful, ColorResult } from '@uiw/react-color';
import { Box, Typography, IconButton, Slider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { hsvaToHexa, hexToHsva } from '@uiw/color-convert';
import { IconLine, IconRectangle, IconOval } from '@tabler/icons-react';
import ColorHistory from './ColorHistory';
import { useColorHistoryContext } from '../contexts/ColorHistoryContext';
import { ColourWrapper } from './ToolsPencil';
import { Segmented, SegmentButton } from './ToolsFill';

type SHAPE_TYPE = 'line' | 'rect' | 'ellipse';
type SHAPES_VIEW = 'main' | 'color' | 'size';

const SHAPES: { type: SHAPE_TYPE; label: string; Icon: any }[] = [
	{ type: 'line', label: 'Line', Icon: IconLine },
	{ type: 'rect', label: 'Rectangle', Icon: IconRectangle },
	{ type: 'ellipse', label: 'Ellipse', Icon: IconOval },
];

export type ToolsShapesProps = {
	p5: () => any; // p5 instance
};

function ToolsShapes({ p5 }: ToolsShapesProps) {
	const [view, setView] = useState<SHAPES_VIEW>('main');
	const [shapeType, setShapeType] = useState<SHAPE_TYPE>('line');
	const [filled, setFilled] = useState(false);
	const [snap, setSnap] = useState(false);
	// Shape colour and thickness are shared with the pencil
	const [hsva, setHsva] = useState({ h: 0, s: 0, v: 68, a: 1 });
	const [sizeX, setSizeX] = useState(1);
	const [sizeY, setSizeY] = useState(1);

	const { colorHistory, selectColorFromHistory } = useColorHistoryContext();

	useEffect(() => {
		const color = p5()?.getPencilColourHex();
		if (color) setHsva(hexToHsva(color));

		const size = p5()?.getPensilSize();
		if (size) {
			setSizeX(size.x);
			setSizeY(size.y);
		}

		if (typeof p5()?.getShapeType === 'function') setShapeType(p5().getShapeType());
		if (typeof p5()?.getShapeFilled === 'function') setFilled(!!p5().getShapeFilled());
		if (typeof p5()?.getShapeSnap === 'function') setSnap(!!p5().getShapeSnap());
	}, [p5]);

	const selectShape = (type: SHAPE_TYPE) => {
		setShapeType(type);
		p5().setShapeType(type);
	};

	const selectFilled = (isFilled: boolean) => {
		setFilled(isFilled);
		p5().setShapeFilled(isFilled);
	};

	const selectSnap = (isSnap: boolean) => {
		setSnap(isSnap);
		p5().setShapeSnap(isSnap);
	};

	const updateColor = (color: ColorResult) => {
		setHsva(color.hsva);
		p5().setPencilColour(color.hexa); // shared with the pencil
	};

	const handleColorHistorySelect = (color: string) => {
		setHsva(hexToHsva(color));
		p5().setPencilColour(color);
		selectColorFromHistory(color);
	};

	const updateSize = (axis: 'x' | 'y', value: number) => {
		const size = Math.min(Math.max(1, value), 10);
		const newX = axis === 'x' ? size : sizeX;
		const newY = axis === 'y' ? size : sizeY;
		setSizeX(newX);
		setSizeY(newY);
		p5().setPensilSize(newX, newY); // shared with the pencil
	};

	const shapeHex = hsvaToHexa(hsva);

	return (
		<>
			{view === 'main' && (
				<Box flex={1}>
					<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
						Shapes
					</Typography>

					<Box display="flex" alignItems="center" p={1} m={1} mr={0}>
						<Box flex={1} mr={1}>
							<Typography fontSize={12} fontWeight={600}>
								Draw a Shape
							</Typography>
							<Typography fontSize={10} color="#aaa">
								Drag on the canvas from start to end
							</Typography>
						</Box>

						<Box display="flex" gap="6px">
							<ActionButton onClick={() => setView('color')}>
								<Swatch color={shapeHex} />
								Colour
							</ActionButton>
							<ActionButton onClick={() => setView('size')}>
								{sizeX} x {sizeY}
							</ActionButton>
						</Box>
					</Box>

					{/* Shape selector */}
					<Box px={2} pb={1}>
						<Segmented>
							{SHAPES.map(({ type, label, Icon }) => (
								<ShapeSegment key={type} active={shapeType === type} onClick={() => selectShape(type)}>
									<Icon size={18} />
									{label}
								</ShapeSegment>
							))}
						</Segmented>
					</Box>

					{/* Per-shape options: snap for lines, outline/filled for rect + ellipse */}
					<Box px={2} pb={1.5}>
						{shapeType === 'line' ? (
							<>
								<Typography fontSize={10} fontWeight={600} color="#aaa" mb={0.5}>
									ANGLE
								</Typography>
								<Segmented>
									<SegmentButton active={!snap} onClick={() => selectSnap(false)}>
										Free
									</SegmentButton>
									<SegmentButton active={snap} onClick={() => selectSnap(true)}>
										Straighten (45°)
									</SegmentButton>
								</Segmented>
							</>
						) : (
							<>
								<Typography fontSize={10} fontWeight={600} color="#aaa" mb={0.5}>
									STYLE
								</Typography>
								<Segmented>
									<SegmentButton active={!filled} onClick={() => selectFilled(false)}>
										Outline
									</SegmentButton>
									<SegmentButton active={filled} onClick={() => selectFilled(true)}>
										Filled
									</SegmentButton>
								</Segmented>
							</>
						)}
					</Box>
				</Box>
			)}

			{view === 'color' && (
				<ColourWrapper>
					<Box display="flex" justifyContent="space-between" alignItems="top">
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Specify Shape Colour
						</Typography>
						<IconButton size="small" onClick={() => setView('main')}>
							<CloseIcon sx={{ color: '#fff' }} />
						</IconButton>
					</Box>

					<Box px={1} pt={1}>
						<ColorHistory colors={colorHistory} onColorSelect={handleColorHistorySelect} currentColor={shapeHex} />
					</Box>

					<Colorful style={{ padding: 20, width: '100%' }} color={hsva} onChange={updateColor} />
				</ColourWrapper>
			)}

			{view === 'size' && (
				<Box flex={1}>
					<Box display="flex" justifyContent="space-between" alignItems="top">
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Specify Line Thickness
						</Typography>
						<IconButton size="small" onClick={() => setView('main')}>
							<CloseIcon sx={{ color: '#fff' }} />
						</IconButton>
					</Box>

					<Typography mx={2} mt={0.5} fontSize={10} color="#aaa">
						Applies to lines and outlines (shared with the pencil). Filled shapes always cover their full area.
					</Typography>

					<Box px={1} pt={1} pb={1}>
						<SliderRow>
							<SliderLabel>Width</SliderLabel>
							<StyledSlider value={sizeX} onChange={(_, value) => updateSize('x', value as number)} min={1} max={10} step={1} marks size="small" />
							<SizeIndicator>{sizeX}</SizeIndicator>
						</SliderRow>
						<SliderRow>
							<SliderLabel>Height</SliderLabel>
							<StyledSlider value={sizeY} onChange={(_, value) => updateSize('y', value as number)} min={1} max={10} step={1} marks size="small" />
							<SizeIndicator>{sizeY}</SizeIndicator>
						</SliderRow>
					</Box>
				</Box>
			)}
		</>
	);
}

export default ToolsShapes;

const ActionButton = styled.button`
	display: flex;
	align-items: center;
	gap: 6px;
	background-color: #444;
	color: white;
	border: none;
	border-radius: 6px;
	padding: 6px 10px;
	font-size: 11px;
	font-weight: 600;
	cursor: pointer;

	&:hover {
		background-color: #555;
	}

	&:active {
		transform: scale(0.97);
	}
`;

const Swatch = styled.div<{ color: string }>`
	width: 16px;
	height: 16px;
	border-radius: 4px;
	border: 1px solid #777;
	background-color: ${props => props.color};
`;

const ShapeSegment = styled(SegmentButton)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 5px;
`;

const SliderRow = styled.div`
	display: flex;
	align-items: center;
`;

const SliderLabel = styled(Typography)`
	min-width: 40px;
	font-size: 11px;
`;

const StyledSlider = styled(Slider)`
	flex: 1;
	margin: 0 17px 0 10px;
	color: #aaa;

	.MuiSlider-rail {
		background-color: #444;
	}

	.MuiSlider-mark {
		background-color: #666;
		height: 3px;
	}

	.MuiSlider-thumb {
		width: 16px;
		height: 16px;
	}
`;

const SizeIndicator = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background-color: #444;
	color: white;
	font-size: 11px;
	font-weight: bold;
`;
